import { Calendar, MapPin, Clock, DollarSign, Rocket, Eye, BarChart3, Globe } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { formatPrice, getEventTime } from '../lib/helpers';
import { CATEGORY_LABELS } from '../constants/categories';


export const EventCard = ({ event, actions, onPreview }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'inline-flex items-center rounded-full border border-green-200 bg-green-50 px-2.5 py-0.5 text-xs font-semibold text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300';
      case 'pending':
        return 'inline-flex items-center rounded-full border border-yellow-200 bg-yellow-50 px-2.5 py-0.5 text-xs font-semibold text-yellow-700 dark:border-yellow-800 dark:bg-yellow-950 dark:text-yellow-300';
      case 'rejected':
        return 'inline-flex items-center rounded-full border border-red-200 bg-red-50 px-2.5 py-0.5 text-xs font-semibold text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300';
      default:
        return '';
    }
  };

  const formatarPeriodoEvento = (startDate, endDate) => {
  if (!startDate || !endDate) return '';

  const start = startDate.toDate ? startDate.toDate() : new Date(startDate);
  const end = endDate.toDate ? endDate.toDate() : new Date(endDate);

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  const formatShort = (d) =>
    `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;

  const isSameDay = startDay.getTime() === endDay.getTime();

  if (isSameDay) {
    return startDay.getTime() === today.getTime() ? 'Hoje' : formatShort(start);
  }

  if (startDay.getTime() === today.getTime()) {
    return `Hoje até ${formatShort(end)}`;
  }

  if (startDay.getTime() === tomorrow.getTime()) {
    return `Amanhã até ${formatShort(end)}`;
  }

  return `${formatShort(start)} até ${formatShort(end)}`;
};


  // ========================================
  // BOOST STATUS - Using correct Firestore fields
  // Fields: boosted, boostType, boostStart, boostEnd, boostStatus
  // ========================================
  
  // Check if event is currently boosted: boosted === true AND boostEnd > now
  const isBoosted = event.boosted === true;
  
  const getBoostEndDate = () => {
    if (!event.boostEnd) return null;
    return event.boostEnd.toDate ? event.boostEnd.toDate() : new Date(event.boostEnd);
  };
  
  const boostEndDate = getBoostEndDate();
  const isBoostActive = isBoosted && boostEndDate && boostEndDate > new Date();
  
  // Get remaining time for boost
  const getBoostRemainingTime = () => {
    if (!boostEndDate || boostEndDate <= new Date()) return null;
    return formatDistanceToNow(boostEndDate, { locale: ptBR, addSuffix: false });
  };

  const boostRemainingTime = getBoostRemainingTime();
  
  // Views: únicas (por usuário logado) e totais (bruto, sem dedupe)
  const viewsUnique = event.viewsUnique || 0;
  const viewsTotal = event.views || 0;
  // Views do site (página do evento em joynapp.com.br), por navegador
  const webViewsUnique = event.webViewsUnique || 0;

  return (
    <div 
      className={`border ${
        isBoostActive
          ? 'border-orange-500 shadow-lg shadow-orange-500/20 ring-2 ring-orange-500/30'
          : 'border-border'
      } bg-card text-card-foreground rounded-xl transition-all hover:border-primary/20 hover:scale-[1.02] overflow-hidden relative cursor-pointer`}
      data-testid={`event-card-${event.id}`}
      onClick={onPreview}
    >
      {/* Image - Clean, no text overlay */}
      {event.imageUrl && (
        <div className="aspect-video w-full overflow-hidden relative">
          <img
            src={event.imageUrl}
            alt={event.title}
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover transition-transform hover:scale-105 duration-300"
          />
          {/* Boost badge - positioned at bottom right of image with semi-transparent background */}
          {isBoostActive && (
            <div className="absolute bottom-2 right-2 flex flex-col items-end gap-1">
              <div className="bg-gradient-to-r from-orange-500 via-red-500 to-pink-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg flex items-center gap-1.5">
                <Rocket className="w-3.5 h-3.5" />
                Impulsionado
              </div>
              {boostRemainingTime && (
                <div className="bg-black/80 text-white px-2 py-0.5 rounded-full text-[10px] font-medium flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {boostRemainingTime}
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Content - All info below image */}
      <div className="p-5 space-y-4">
        {/* Title and Status */}
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-xl font-semibold tracking-tight line-clamp-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {event.title}
          </h3>
          <span className={getStatusColor(event.status)} data-testid={`status-badge-${event.status}`}>
            {event.status === 'approved' ? 'Aprovado' : event.status === 'pending' ? 'Pendente' : 'Rejeitado'}
          </span>
        </div>

        {/* Description */}
        <p className="text-sm text-muted-foreground line-clamp-2">{event.description}</p>

        {/* Event details */}
        <div className="space-y-2">
          {/* Date */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Calendar className="w-4 h-4 shrink-0" />
            <span>{formatarPeriodoEvento(event.startDate, event.endDate)}</span>
          </div>
          
          {/* Time */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="w-4 h-4 shrink-0" />
            <span>{getEventTime(event)}</span>
          </div>
          
          {/* Location */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="w-4 h-4 shrink-0" />
            <span className="truncate">{event.city} - {event.location}</span>
          </div>
        </div>

        {/* Tags row: Category, Price, Views */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Category */}
          {event.category && (
            <span className="px-2 py-1 bg-secondary rounded-md text-xs font-medium">
              {CATEGORY_LABELS[event.category] || event.category}
            </span>
          )}
          
          {/* Price - Green for free, different style for paid */}
          {event.isFree ? (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300 rounded-md text-xs font-semibold border border-green-200 dark:border-green-800">
              Gratuito
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-950 text-blue-700 dark:text-blue-300 rounded-md text-xs font-medium">
              <DollarSign className="w-3 h-3" />
              {formatPrice(event.ticketPrice)}
            </span>
          )}
          
          {/* Views count - únicas e totais lado a lado */}
          {viewsUnique > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded-md text-xs font-medium text-muted-foreground">
              <Eye className="w-3 h-3" />
              {viewsUnique} únicas
            </span>
          )}
          {viewsTotal > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded-md text-xs font-medium text-muted-foreground">
              <BarChart3 className="w-3 h-3" />
              {viewsTotal} totais
            </span>
          )}
          {webViewsUnique > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-1 bg-muted rounded-md text-xs font-medium text-muted-foreground">
              <Globe className="w-3 h-3" />
              {webViewsUnique} site
            </span>
          )}
        </div>

        {/* Action buttons */}
        {actions && <div className="flex flex-wrap gap-2 pt-2" onClick={(e) => e.stopPropagation()}>{actions}</div>}
      </div>
    </div>
  );
};

